import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Settings } from 'lucide-react';
import { useToast } from '@centraoneplus/ui';
import { Button, Input, Notice, StatusChip } from '@shared/ui/kit';
import { useAuthStore } from '@stores/authStore';
import { tenantService } from '@services/tenant.service';
import { SettingsCard, SectionHead, FieldLabel, ReadOnlyValue, LoadingBlock } from './chrome';

// Extended type for the API response which includes DB fields not in the base Tenant type
interface OrgDetails {
  id: string;
  name: string;
  slug: string;
  domain?: string;
  logoUrl?: string;
  settings?: any;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Organization identity — name, slug, domain, plan, status.
 *
 * Mounted twice: as the `Info` section of Organization Settings (`embedded`),
 * and standalone at `/settings/organization` for viewers who can see their
 * organization but not manage it. The only difference is the shortcut INTO
 * Organization Settings, which is circular once we are already inside it.
 */
export default function OrganizationSettings({ embedded = false }: { embedded?: boolean } = {}) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const toast = useToast();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';

  const [org, setOrg] = useState<OrgDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);

  // Editable fields
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');

  // Inside Organization Settings the rail already says "Organization", so the
  // section is named for what it holds; standalone it is the whole page.
  const heading = embedded ? 'Info' : 'Organization';

  useEffect(() => {
    loadOrganization();
  }, []);

  const loadOrganization = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await tenantService.getMyOrganization();
      setOrg(data);
      setName(data.name || '');
      setDomain(data.domain || '');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load organization details');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!org) return;
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const updated = await tenantService.updateMyOrganization({ name, domain });
      setOrg(updated);
      setEditing(false);
      setSuccess('Organization updated successfully');
      setTimeout(() => setSuccess(''), 3000);
      toast.success('Organization updated');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update organization');
      toast.error('Failed to update organization', err?.response?.data?.message || err?.response?.data?.error?.message || err?.message || 'Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (org) {
      setName(org.name || '');
      setDomain(org.domain || '');
    }
    setEditing(false);
    setError('');
  };

  if (loading) {
    return (
      <div className="flex flex-col gap-[14px]">
        <SettingsCard>
          <SectionHead title={heading} description="View and manage your organization details." />
          <div className="mt-4 space-y-[10px]">
            <LoadingBlock height={34} />
            <LoadingBlock height={34} />
            <LoadingBlock height={34} />
          </div>
        </SettingsCard>
      </div>
    );
  }

  if (error && !org) {
    return (
      <div className="flex flex-col gap-[14px]">
        <SettingsCard>
          <SectionHead title={heading} description="View and manage your organization details." />
          <div className="mt-4">
            <Notice
              tone="danger"
              headline="Could not load the organization"
              body={error}
              action={{ label: 'Retry', onClick: loadOrganization }}
            />
          </div>
        </SettingsCard>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[14px]">
      <SettingsCard>
        <SectionHead
          title={heading}
          description="Who this organization is, as the platform holds it. The name and domain are what customers see on outbound mail and the portal."
          actions={
            isAdmin && !editing ? (
              <>
                <Button onClick={() => setEditing(true)}>Edit</Button>
                {/* Shown only on the standalone page — inside Organization
                    Settings this would navigate to the screen we are on. */}
                {!embedded && user?.tenantId && (
                  <Button onClick={() => navigate(`/settings/tenant/${user.tenantId}/general`)}>
                    <Settings className="mr-1.5 h-4 w-4" />
                    Organization Settings
                  </Button>
                )}
              </>
            ) : undefined
          }
        />
      </SettingsCard>

      {error && (
        <Notice tone="danger" headline="Could not save" body={error} />
      )}

      {success && (
        <Notice tone="ok" headline="Saved" body={success} />
      )}

      <SettingsCard>
        <SectionHead
          title={org?.name || 'Organization'}
          description={
            <span className="co-mono text-[11px] tracking-[.04em]">ID: {org?.id}</span>
          }
          actions={
            org?.isActive !== undefined ? (
              <StatusChip tone={org.isActive ? 'ok' : 'danger'}>
                {org.isActive ? 'Active' : 'Inactive'}
              </StatusChip>
            ) : undefined
          }
        />

        <div className="mt-4 flex flex-wrap gap-[14px]">
          {/* Name */}
          <div className="min-w-[240px] flex-1">
            <FieldLabel htmlFor="org-name">Organization name</FieldLabel>
            <div className="mt-[7px]">
              {editing ? (
                <Input
                  id="org-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              ) : (
                <ReadOnlyValue>{org?.name || '-'}</ReadOnlyValue>
              )}
            </div>
          </div>

          {/* Slug (always read-only) */}
          <div className="w-[260px]">
            <FieldLabel>Slug</FieldLabel>
            <div className="mt-[7px]">
              <ReadOnlyValue mono>{org?.slug || '-'}</ReadOnlyValue>
            </div>
          </div>
        </div>

        <div className="mt-[14px] flex flex-wrap gap-[14px] border-t border-[var(--co-line-subtle)] pt-[14px]">
          {/* Domain */}
          <div className="min-w-[240px] flex-1">
            <FieldLabel htmlFor="org-domain">Domain</FieldLabel>
            <div className="mt-[7px]">
              {editing ? (
                <Input
                  id="org-domain"
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  placeholder="e.g., yourcompany.com"
                />
              ) : (
                <ReadOnlyValue>{org?.domain || '-'}</ReadOnlyValue>
              )}
            </div>
          </div>

          {/* Created */}
          <div className="w-[260px]">
            <FieldLabel>Created</FieldLabel>
            <div className="mt-[7px]">
              <ReadOnlyValue>
                {org?.createdAt ? new Date(org.createdAt).toLocaleDateString() : '-'}
              </ReadOnlyValue>
            </div>
          </div>
        </div>

        <div className="mt-[14px] flex flex-wrap items-start gap-[14px] border-t border-[var(--co-line-subtle)] pt-[14px]">
          {/* Subscription Tier */}
          <div className="min-w-[240px] flex-1">
            <FieldLabel>Plan</FieldLabel>
            <div className="mt-[7px]">
              <StatusChip tone="neutral">{org?.subscriptionTier || 'free'}</StatusChip>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="w-[260px]">
            <FieldLabel>Subscription status</FieldLabel>
            <div className="mt-[7px]">
              <StatusChip tone={org?.subscriptionStatus === 'active' ? 'ok' : 'warn'}>
                {org?.subscriptionStatus || 'unknown'}
              </StatusChip>
            </div>
          </div>
        </div>

        {/* Edit Actions */}
        {editing && (
          <div className="mt-[14px] flex items-center gap-3 border-t border-[var(--co-line-subtle)] pt-[14px]">
            <Button variant="primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <Button onClick={handleCancel} disabled={saving}>
              Cancel
            </Button>
          </div>
        )}
      </SettingsCard>
    </div>
  );
}
